import { z } from 'zod';
import { CreativeFormat } from '../enums';
import { hexColorSchema } from './common';

/* ------------------------------------------------------------------------- *
 * CAMADA 1 — imagem base gerada por IA. NUNCA contém texto.
 * ------------------------------------------------------------------------- */

export const imagePromptSchema = z.object({
  /** Prompt em inglês costuma render melhor no gerador de imagem. */
  prompt: z.string().max(2000),
  negativePrompt: z.string().max(1000),
  scene: z.string().max(500),
  mood: z.string().max(200),
  /** Onde o texto será sobreposto — a IA deve deixar essa área limpa. */
  safeArea: z.enum(['TOP', 'BOTTOM', 'LEFT', 'RIGHT', 'CENTER']),
});
export type ImagePrompt = z.infer<typeof imagePromptSchema>;

/* ------------------------------------------------------------------------- *
 * CAMADA 2 — composição determinística. Preço, logo e CTA JAMAIS vêm da IA.
 * ------------------------------------------------------------------------- */

export const creativeContentSchema = z.object({
  headline: z.string().max(80),
  subheadline: z.string().max(120).optional(),
  speedLabel: z.string().max(40).optional(),
  priceCents: z.number().int().min(0).optional(),
  pricePrefix: z.string().max(20).default('a partir de'),
  priceSuffix: z.string().max(20).default('/mês'),
  benefits: z.array(z.string().max(60)).max(5).default([]),
  cta: z.string().max(40),
  whatsapp: z.string().max(20).optional(),
  website: z.string().max(120).optional(),
  disclaimer: z.string().max(300).optional(),
});
export type CreativeContent = z.infer<typeof creativeContentSchema>;

/* ------------------------------------------------------------------------- *
 * TEXTOS POR IA para a composição de duas camadas.
 *
 * A IA escreve SÓ o texto editorial (título, subtítulo, CTA, benefícios) — o
 * preço e a velocidade continuam vindo do PLANO real no render. Assim dá para
 * gerar vários criativos com o mesmo template/fundo e copy diferente.
 * ------------------------------------------------------------------------- */
export const creativeCopyVariantSchema = z.object({
  headline: z.string().min(1).max(80),
  subheadline: z.string().max(120).optional(),
  cta: z.string().min(1).max(40),
  benefits: z.array(z.string().max(60)).max(5).default([]),
});
export type CreativeCopyVariant = z.infer<typeof creativeCopyVariantSchema>;

export const creativeCopyGenerationSchema = z.object({
  variants: z.array(creativeCopyVariantSchema).min(1).max(5),
});
export type CreativeCopyGeneration = z.infer<typeof creativeCopyGenerationSchema>;

export const generateCreativeCopySchema = z.object({
  clientId: z.string().uuid(),
  /** Plano vinculado: dá contexto real (velocidade, benefícios) à IA. */
  planId: z.string().uuid().optional(),
  /** Ângulo/tema opcional para direcionar a copy (ex.: "foco em gamers"). */
  angle: z.string().max(500).optional(),
  count: z.number().int().min(1).max(5).default(3),
  extraInstructions: z.string().max(1000).optional(),
});
export type GenerateCreativeCopyInput = z.infer<typeof generateCreativeCopySchema>;

export const renderCreativeSchema = z.object({
  clientId: z.string().uuid(),
  templateId: z.string().uuid(),
  format: z.nativeEnum(CreativeFormat),
  /** Storage key da imagem base (gerada por IA ou enviada). */
  backgroundKey: z.string().min(1).max(500).optional(),
  content: creativeContentSchema,
  overrides: z
    .object({
      primaryColor: hexColorSchema.optional(),
      secondaryColor: hexColorSchema.optional(),
      textColor: hexColorSchema.optional(),
      logoAssetId: z.string().uuid().optional(),
      overlayOpacity: z.number().min(0).max(1).optional(),
    })
    .default({}),
  planId: z.string().uuid().optional(),
});
export type RenderCreativeInput = z.infer<typeof renderCreativeSchema>;

/* ------------------------------------------------------------------------- *
 * TEMPLATES — áreas editáveis com margens de segurança.
 * ------------------------------------------------------------------------- */

export const templateZoneSchema = z.object({
  key: z.enum([
    'logo',
    'headline',
    'subheadline',
    'speed',
    'price',
    'benefits',
    'cta',
    'whatsapp',
    'website',
    'disclaimer',
  ]),
  /** Coordenadas em porcentagem (0–100) da largura/altura do formato. */
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  width: z.number().min(1).max(100),
  height: z.number().min(1).max(100),
  align: z.enum(['left', 'center', 'right']).default('left'),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
  fontSizePct: z.number().min(0.5).max(30).default(4),
  fontWeight: z.number().int().min(100).max(900).default(700),
  colorRole: z.enum(['primary', 'secondary', 'accent', 'onPrimary', 'onBackground', 'custom']),
  customColor: hexColorSchema.optional(),
  uppercase: z.boolean().default(false),
  maxLines: z.number().int().min(1).max(6).default(2),
  visible: z.boolean().default(true),

  /* --- Fundo da zona (pílula, caixa de preço, botão CTA). --- *
   * Ausente ou 'none' preserva o comportamento antigo (só texto). Qualquer
   * outro valor desenha um retângulo preenchido ATRÁS do texto — o "botão".
   * Optional (sem default) de propósito: mantém compatível com os templates já
   * salvos, cujas zonas não têm esses campos. */
  background: z
    .enum(['none', 'primary', 'secondary', 'accent', 'onPrimary', 'onBackground', 'custom'])
    .optional(),
  backgroundColor: hexColorSchema.optional(),
  /** Raio do canto como % da altura da zona (50 = pílula totalmente arredondada). */
  cornerRadiusPct: z.number().min(0).max(50).optional(),
  /** Opacidade do fundo — permite caixas semitransparentes sobre a foto. */
  backgroundOpacity: z.number().min(0).max(1).optional(),

  /* --- Gradientes. --- *
   * Se `...From` e `...To` estiverem preenchidos, o fundo do botão / o texto do
   * título usa um gradiente linear em vez de cor sólida. Ângulo em graus:
   * 0 = esquerda→direita, 90 = cima→baixo. */
  backgroundGradientFrom: hexColorSchema.optional(),
  backgroundGradientTo: hexColorSchema.optional(),
  backgroundGradientAngle: z.number().min(0).max(360).optional(),
  textGradientFrom: hexColorSchema.optional(),
  textGradientTo: hexColorSchema.optional(),
  textGradientAngle: z.number().min(0).max(360).optional(),
});
export type TemplateZone = z.infer<typeof templateZoneSchema>;

/* ------------------------------------------------------------------------- *
 * Tipografia — fontes do Google disponibilizadas para os criativos.
 * O renderizador (sharp/SVG) resolve pelo nome da família; as fontes precisam
 * estar instaladas na imagem da API (ver apps/api/Dockerfile).
 * ------------------------------------------------------------------------- */
export const CREATIVE_FONTS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
] as const;

export type CreativeFontFamily = (typeof CREATIVE_FONTS)[number]['value'];
export const CREATIVE_FONT_FAMILIES: readonly string[] = CREATIVE_FONTS.map((f) => f.value);

export const creativeTemplateSchema = z.object({
  name: z.string().trim().min(2).max(160),
  description: z.string().max(1000).optional(),
  format: z.nativeEnum(CreativeFormat),
  /** Margem de segurança em % — nada é renderizado fora dela. */
  safeMarginPct: z.number().min(0).max(20).default(6),
  overlayOpacity: z.number().min(0).max(1).default(0.45),
  overlayDirection: z.enum(['bottom', 'top', 'left', 'right', 'full']).default('bottom'),
  zones: z.array(templateZoneSchema).min(1).max(12),
  isGlobal: z.boolean().default(false),
  clientId: z.string().uuid().optional(),
  isActive: z.boolean().default(true),
});
export type CreativeTemplateInput = z.infer<typeof creativeTemplateSchema>;

export const generateImageSchema = z.object({
  clientId: z.string().uuid(),
  format: z.nativeEnum(CreativeFormat),
  scene: z.string().min(5).max(1000),
  safeArea: z.enum(['TOP', 'BOTTOM', 'LEFT', 'RIGHT', 'CENTER']).default('BOTTOM'),
  count: z.number().int().min(1).max(4).default(1),
  /** Estilo da biblioteca de artes. Ausente = cena livre, sem estilo. */
  artStyle: z.string().max(40).optional(),
  accentColorHex: hexColorSchema.optional(),
  /** Storage keys de imagens de referência enviadas pelo operador. */
  referenceKeys: z.array(z.string().max(500)).max(4).default([]),
  /** Sobrescreve o modelo configurado na organização. */
  model: z.string().max(60).optional(),
});
export type GenerateImageInput = z.infer<typeof generateImageSchema>;

/* ------------------------------------------------------------------------- *
 * ARTE COMPLETA — a IA desenha também os textos.
 *
 * Sai do contrato de duas camadas de propósito: é o modo que reproduz peças
 * como as de referência, onde tipografia e cena são inseparáveis. Em troca, o
 * preço passa a ser desenhado pelo modelo, e por isso a conferência humana é
 * OBRIGATÓRIA antes de qualquer publicação.
 * ------------------------------------------------------------------------- */

export const fullArtTextsSchema = z.object({
  headline: z.string().max(120).optional(),
  subheadline: z.string().max(160).optional(),
  speedLabel: z.string().max(40).optional(),
  priceText: z.string().max(40).optional(),
  badge: z.string().max(30).optional(),
  benefits: z.array(z.string().max(60)).max(6).default([]),
  cta: z.string().max(40).optional(),
  whatsapp: z.string().max(30).optional(),
  website: z.string().max(120).optional(),
  disclaimer: z.string().max(200).optional(),
});
export type FullArtTexts = z.infer<typeof fullArtTextsSchema>;

/**
 * O que fica salvo em `Creative.content` quando a peça vem do modo ARTE COMPLETA.
 *
 * Guarda TUDO que o painel precisa para reabrir a geração e continuar editando:
 * os parâmetros originais e as imagens produzidas. É o que faltava — antes a
 * arte completa não gerava registro nenhum e sumia ao recarregar a página.
 */
export const fullArtImageSchema = z.object({
  storageKey: z.string(),
  publicUrl: z.string(),
});

export const fullArtCreativeContentSchema = z.object({
  kind: z.literal('full_art'),
  artStyle: z.string(),
  instructions: z.string().default(''),
  accentColorHex: z.string(),
  planId: z.string().uuid().nullable().default(null),
  texts: fullArtTextsSchema.default({}),
  referenceKeys: z.array(z.string()).default([]),
  model: z.string().default(''),
  count: z.number().int().default(1),
  images: z.array(fullArtImageSchema).default([]),
  precoEsperado: z.string().nullable().default(null),
});
export type FullArtCreativeContent = z.infer<typeof fullArtCreativeContentSchema>;

export const generateFullArtSchema = z.object({
  clientId: z.string().uuid(),
  format: z.nativeEnum(CreativeFormat),
  artStyle: z.string().max(40),
  /** Bloco livre: público, clima, elementos desejados. */
  instructions: z.string().max(2000).default(''),
  accentColorHex: hexColorSchema.optional(),
  /** Quando informado, os textos são derivados do plano REAL do banco. */
  planId: z.string().uuid().optional(),
  /** Textos explícitos. Sobrescrevem o que vier do plano. */
  texts: fullArtTextsSchema.default({}),
  referenceKeys: z.array(z.string().max(500)).max(4).default([]),
  model: z.string().max(60).optional(),
  count: z.number().int().min(1).max(4).default(1),
});
export type GenerateFullArtInput = z.infer<typeof generateFullArtSchema>;

/** Modelos de imagem que a plataforma aceita. */
export const IMAGE_MODEL_FALLBACK = 'gpt-image-2';

export const imageModelSettingSchema = z.object({
  model: z.string().min(3).max(60),
});
export type ImageModelSettingInput = z.infer<typeof imageModelSettingSchema>;
